import { useState, useRef, useCallback, useEffect } from "react";
import {
  saveAudioChunk,
  getAudioChunks,
  clearInterviewAudio,
  deleteAudioChunk,
  getPendingSubmissions,
} from "../services/indexedDBService";
/**
 * Recording strategy:
 * - Audio is recorded continuously using MediaRecorder.
 * - Audio data is accumulated in memory (`chunksRef`).
 * - Every CHUNK_INTERVAL milliseconds, the accumulated audio
 *   is persisted into IndexedDB to avoid large in-memory blobs
 *   and to prevent data loss on long recordings.
 * - Multiple recordings can exist per interview session and
 *   are managed independently for better UX (delete/undo).
 */

const CHUNK_INTERVAL = 120000; // 2 minutes
const AUDIO_BITRATE = 32000; // 32kbps

/**
 * Determines the best supported audio MIME type for the current browser.
 * Priority is given to Opus in WebM when available.
 */
const getMimeType = () => {
  if (typeof MediaRecorder === "undefined") return null;
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
    return "audio/webm;codecs=opus";
  }
  if (MediaRecorder.isTypeSupported("audio/webm")) {
    return "audio/webm";
  }
  if (MediaRecorder.isTypeSupported("audio/mp4")) {
    return "audio/mp4";
  }
  return "audio/webm";
};

export const useAudioRecorder = (interviewId, alias) => {
  // ---- UI / Public State ----
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [isSupported, setIsSupported] = useState(true);

  // Completed recordings for the current session
  const [recordedAudios, setRecordedAudios] = useState([]);
  const [hasUnsavedRecordings, setHasUnsavedRecordings] = useState(false);

  // Audios already persisted on the server (or pending sync) shown for context.
  // Live alongside session recordings but are tracked separately because:
  //   - their `url` is a server-signed URL, not a Blob URL (do not revoke)
  //   - they must NOT be cleared by clearAllRecordings on save success
  const [loadedAudios, setLoadedAudiosState] = useState([]);
  const [isLoadingAudios, setIsLoadingAudios] = useState(true);

  // Audios that were saved offline (queued in pendingSubmissions) but haven't
  // been uploaded yet. Shown read-only so the interviewer knows what's pending.
  const [offlinePendingAudios, setOfflinePendingAudios] = useState([]);

  // ---- Internal mutable refs (do not trigger re-renders) ----
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const chunkIndexRef = useRef(0);
  const chunkStartTimeRef = useRef(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);

  /**
   * Accumulates total paused time (in ms).
   * When pausing, we subtract Date.now().
   * When resuming, we add Date.now().
   * This allows us to calculate accurate elapsed recording time.
   */
  const pausedDurationRef = useRef(0);

  // Used to generate stable IDs for in-session recordings
  const recordingIdRef = useRef(0);

  // ---- Capability check ----
  useEffect(() => {
    const supported =
      typeof MediaRecorder !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia;

    setIsSupported(supported);

    if (!supported) {
      setError("recording_not_supported");
    }
  }, []);

  // ---- Rehydrate from IndexedDB on mount ----
  // Two sources to recover from:
  //   1. audioRecordings: chunks of an interrupted recording (browser closed/reload)
  //   2. pendingSubmissions: audios saved offline awaiting sync
  useEffect(() => {
    if (!interviewId) return;
    let cancelled = false;

    (async () => {
      try {
        // 1. Orphan chunks → merge into one recoverable session audio
        const chunks = await getAudioChunks(interviewId);
        const ownChunks = alias
          ? chunks.filter((c) => c.alias === alias)
          : chunks;
        if (!cancelled && ownChunks.length > 0) {
          const blobs = ownChunks.map((c) => c.blob);
          const merged = new Blob(blobs, {
            type: blobs[0]?.type || getMimeType(),
          });
          const totalDuration = ownChunks.reduce(
            (sum, c) => sum + (c.duration || 0),
            0,
          );
          const recoveredAudio = {
            id: `recovered-${Date.now()}`,
            blob: merged,
            duration: totalDuration,
            createdAt: ownChunks[0].createdAt || new Date().toISOString(),
            url: URL.createObjectURL(merged),
            source: "recovered",
            chunkIds: ownChunks.map((c) => c.id),
          };
          setRecordedAudios((prev) => {
            // Avoid double-adding if effect re-runs
            if (prev.some((a) => a.source === "recovered")) return prev;
            return [recoveredAudio, ...prev];
          });
          setHasUnsavedRecordings(true);
        }

        // 2. Pending submissions → expose their queued audios as read-only
        const submissions = await getPendingSubmissions();
        const matching = submissions.filter(
          (s) => s.interviewId === interviewId && s.alias === alias,
        );
        const pendingItems = matching.flatMap((sub) =>
          (sub.pendingAudios || [])
            .filter((a) => a?.blob)
            .map((audio, idx) => ({
              id: `pending-${sub.id}-${idx}`,
              blob: audio.blob,
              duration: audio.duration || 0,
              url: URL.createObjectURL(audio.blob),
              source: "offline-pending",
              submissionId: sub.id,
            })),
        );
        if (!cancelled) {
          setOfflinePendingAudios(pendingItems);
        }
      } catch (err) {
        console.error("Error rehydrating audios from IndexedDB:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [interviewId, alias]);

  // ---- Cleanup on unmount ----
  useEffect(() => {
    return () => {
      // Detach onstop so it doesn't try to setState on an unmounted component
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.onstop = null;
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          // Already stopped or in an invalid state — safe to ignore
        }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // ---- Utilities ----
  const formatDuration = useCallback((seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
      return `${hrs.toString().padStart(2, "0")}:${mins
        .toString()
        .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }, []);

  // ---- Permissions ----
  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setHasPermission(true);
      setError(null);
      return true;
    } catch (err) {
      setHasPermission(false);
      if (err.name === "NotAllowedError") {
        setError("microphone_permission_denied");
      } else if (err.name === "NotFoundError") {
        setError("microphone_not_available");
      } else {
        setError("microphone_error");
      }
      return false;
    }
  }, []);

  /**
   * Persists the currently accumulated audio chunks into IndexedDB.
   * This function:
   * - Creates a Blob from the in-memory chunks
   * - Saves it with metadata (index, duration)
   * - Clears the buffer to start collecting the next chunk
   */
  const saveCurrentChunk = useCallback(async () => {
    if (chunksRef.current.length === 0) return;

    const blob = new Blob(chunksRef.current, { type: getMimeType() });
    const chunkDuration = (Date.now() - chunkStartTimeRef.current) / 1000;

    try {
      await saveAudioChunk({
        interviewId,
        alias,
        chunkIndex: chunkIndexRef.current,
        blob,
        duration: chunkDuration,
      });

      chunkIndexRef.current += 1;
      chunksRef.current = [];
      chunkStartTimeRef.current = Date.now();
    } catch (err) {
      console.error("Error saving audio chunk:", err);
    }
  }, [interviewId, alias]);

  // ---- Recording lifecycle ----
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError("recording_not_supported");
      return false;
    }

    // Reset first so a repeated failure (same code) still re-notifies the UI
    setError(null);
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: getMimeType(),
        audioBitsPerSecond: AUDIO_BITRATE,
      });

      mediaRecorderRef.current = mediaRecorder;

      chunksRef.current = [];
      chunkIndexRef.current = 0;
      chunkStartTimeRef.current = Date.now();
      startTimeRef.current = Date.now();
      pausedDurationRef.current = 0;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
        setError("recording_error");
        setIsRecording(false);
      };

      // Start recording and emit data every second
      mediaRecorder.start(1000);

      timerRef.current = setInterval(() => {
        const elapsed =
          (Date.now() - startTimeRef.current - pausedDurationRef.current) /
          1000;
        setDuration(elapsed);

        if (
          Date.now() - chunkStartTimeRef.current >= CHUNK_INTERVAL &&
          chunksRef.current.length > 0
        ) {
          saveCurrentChunk();
        }
      }, 1000);

      setIsRecording(true);
      setIsPaused(false);
      setHasPermission(true);
      setError(null);

      return true;
    } catch (err) {
      console.error("Error starting recording:", err);
      // Release the mic if access was granted but MediaRecorder failed to start
      stream?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      mediaRecorderRef.current = null;
      if (err.name === "NotAllowedError") {
        setError("microphone_permission_denied");
        setHasPermission(false);
      } else if (err.name === "NotFoundError") {
        setError("microphone_not_available");
      } else {
        setError("recording_error");
      }
      return false;
    }
  }, [isSupported, saveCurrentChunk]);

  const pauseRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);

      // Start pause duration accumulation
      pausedDurationRef.current -= Date.now();

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      // End pause duration accumulation
      pausedDurationRef.current += Date.now();

      mediaRecorderRef.current.resume();
      setIsPaused(false);

      timerRef.current = setInterval(() => {
        const elapsed =
          (Date.now() - startTimeRef.current - pausedDurationRef.current) /
          1000;
        setDuration(elapsed);

        if (
          Date.now() - chunkStartTimeRef.current >= CHUNK_INTERVAL &&
          chunksRef.current.length > 0
        ) {
          saveCurrentChunk();
        }
      }, 1000);
    }
  }, [saveCurrentChunk]);

  const stopRecording = useCallback(
    async (saveToList = true) => {
      return new Promise((resolve) => {
        if (!mediaRecorderRef.current) {
          resolve(null);
          return;
        }

        const mediaRecorder = mediaRecorderRef.current;
        const recordingDuration = duration;

        mediaRecorder.onstop = async () => {
          let audioBlob = null;

          if (chunksRef.current.length > 0) {
            // Create blob from current chunks
            const mimeType = getMimeType();
            audioBlob = new Blob(chunksRef.current, { type: mimeType });

            // Save to IndexedDB
            await saveCurrentChunk();
          }

          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
          }

          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }

          // Add to recorded audios list if saveToList is true and we have audio
          if (saveToList && audioBlob && recordingDuration > 0) {
            const newAudio = {
              id: `audio-${recordingIdRef.current++}-${Date.now()}`,
              blob: audioBlob,
              duration: recordingDuration,
              createdAt: new Date().toISOString(),
              url: URL.createObjectURL(audioBlob),
            };
            setRecordedAudios((prev) => [...prev, newAudio]);
            setHasUnsavedRecordings(true);
          }

          setIsRecording(false);
          setIsPaused(false);
          setDuration(0);
          mediaRecorderRef.current = null;
          chunksRef.current = [];

          resolve(audioBlob);
        };

        if (mediaRecorder.state !== "inactive") {
          mediaRecorder.stop();
        } else {
          resolve(null);
        }
      });
    },
    [saveCurrentChunk, duration]
  );

  // Delete a specific recorded audio (works for both session and loaded lists)
  const deleteRecordedAudio = useCallback((audioId) => {
    // Server URLs in loadedAudios are NOT Blob URLs — never revoke them.
    setLoadedAudiosState((prev) => {
      if (!prev.some((a) => a.id === audioId)) return prev;
      return prev.filter((a) => a.id !== audioId);
    });

    setRecordedAudios((prev) => {
      const audio = prev.find((a) => a.id === audioId);
      if (!audio) return prev;
      if (audio.url) {
        URL.revokeObjectURL(audio.url);
      }
      // Recovered audios are backed by raw chunks in IndexedDB. If we don't
      // delete them here, the next mount would re-recover and re-show the
      // same audio after the user explicitly removed it.
      if (audio.source === "recovered" && Array.isArray(audio.chunkIds)) {
        Promise.all(audio.chunkIds.map((cid) => deleteAudioChunk(cid))).catch(
          (err) =>
            console.error("Error deleting recovered audio chunks:", err),
        );
      }
      const updated = prev.filter((a) => a.id !== audioId);
      if (updated.length === 0) {
        setHasUnsavedRecordings(false);
      }
      return updated;
    });
  }, []);

  // Restore a deleted audio (for undo functionality)
  const restoreAudio = useCallback((audio, originalIndex) => {
    if (!audio) return;

    const insertAt = (arr) => {
      if (typeof originalIndex === "number" && originalIndex >= 0) {
        const newArray = [...arr];
        const insertIndex = Math.min(originalIndex, newArray.length);
        newArray.splice(insertIndex, 0, audio);
        return newArray;
      }
      return [...arr, audio];
    };

    // Loaded audios keep their server URL — no Blob URL to recreate
    if (audio.source === "loaded") {
      setLoadedAudiosState((prev) => insertAt(prev));
      return;
    }

    // Session recordings: recreate the Blob URL
    const restoredAudio = {
      ...audio,
      url: audio.blob ? URL.createObjectURL(audio.blob) : audio.url,
    };
    setRecordedAudios((prev) => {
      if (typeof originalIndex === "number" && originalIndex >= 0) {
        const newArray = [...prev];
        const insertIndex = Math.min(originalIndex, newArray.length);
        newArray.splice(insertIndex, 0, restoredAudio);
        return newArray;
      }
      return [...prev, restoredAudio];
    });
    setHasUnsavedRecordings(true);
  }, []);

  // Clear all recordings
  const clearAllRecordings = useCallback(async () => {
    // Revoke all object URLs
    recordedAudios.forEach((audio) => {
      if (audio.url) {
        URL.revokeObjectURL(audio.url);
      }
    });
    setRecordedAudios([]);
    setHasUnsavedRecordings(false);
    // Also clear from IndexedDB
    if (interviewId) {
      await clearInterviewAudio(interviewId);
    }
  }, [recordedAudios, interviewId]);

  // Replace the list of pre-existing audios shown for context.
  // Caller (e.g., ApplyQnA) computes the items from server attachments.
  const setLoadedAudios = useCallback((audios) => {
    setLoadedAudiosState(Array.isArray(audios) ? audios : []);
    setIsLoadingAudios(false);
  }, []);

  // Re-read pending submissions from IndexedDB. Called when the offline-sync
  // queue changes (e.g., a sync completes), so finished items vanish from the
  // list — the new server-saved versions arrive via setLoadedAudios.
  const refreshOfflineAudios = useCallback(async () => {
    if (!interviewId) return;
    try {
      const submissions = await getPendingSubmissions();
      const matching = submissions.filter(
        (s) => s.interviewId === interviewId && s.alias === alias,
      );
      const pendingItems = matching.flatMap((sub) =>
        (sub.pendingAudios || [])
          .filter((a) => a?.blob)
          .map((audio, idx) => ({
            id: `pending-${sub.id}-${idx}`,
            blob: audio.blob,
            duration: audio.duration || 0,
            url: URL.createObjectURL(audio.blob),
            source: "offline-pending",
            submissionId: sub.id,
            pendingIndex: idx,
          })),
      );
      setOfflinePendingAudios((prev) => {
        // Revoke URLs of items we're replacing, to avoid leaks
        const nextIds = new Set(pendingItems.map((p) => p.id));
        prev.forEach((p) => {
          if (!nextIds.has(p.id) && p.url) URL.revokeObjectURL(p.url);
        });
        return pendingItems;
      });
    } catch (err) {
      console.error("Error refreshing offline audios:", err);
    }
  }, [interviewId, alias]);

  // Move just-synced entries from `offlinePendingAudios` to `loadedAudios`,
  // reusing the in-memory Blob URL so playback never breaks. The newly
  // uploaded attachments come from the sync response; we match them to the
  // pending entries by `pendingIndex` (preserved in the same order).
  // No extra GET against the server is needed.
  const promotePendingToLoaded = useCallback(
    (submissionId, uploadedAudios = []) => {
      if (!submissionId) return;
      setOfflinePendingAudios((prev) => {
        const stillPending = [];
        const promoted = [];
        prev.forEach((audio) => {
          if (audio.submissionId !== submissionId) {
            stillPending.push(audio);
            return;
          }
          const uploaded = uploadedAudios[audio.pendingIndex];
          if (!uploaded) {
            // Couldn't match — keep as pending. Next refresh will reconcile.
            stillPending.push(audio);
            return;
          }
          promoted.push({
            id: `loaded-${uploaded.attachments_file}`,
            url: audio.url,
            duration: audio.duration,
            source: "loaded",
            attachments_file: uploaded.attachments_file,
            attachments_data: uploaded.attachments_data,
            name: uploaded.attachments_data?.name,
            blob: audio.blob,
          });
        });
        if (promoted.length > 0) {
          setLoadedAudiosState((prevLoaded) => {
            // Avoid duplicates if the same file_id is already present
            const existingIds = new Set(
              prevLoaded.map((a) => a.attachments_file),
            );
            const additions = promoted.filter(
              (p) => !existingIds.has(p.attachments_file),
            );
            return additions.length > 0
              ? [...prevLoaded, ...additions]
              : prevLoaded;
          });
        }
        return stillPending;
      });
    },
    [],
  );

  const getRecordedChunks = useCallback(async () => {
    try {
      return await getAudioChunks(interviewId);
    } catch (err) {
      console.error("Error getting recorded chunks:", err);
      return [];
    }
  }, [interviewId]);

  const toggleRecording = useCallback(async () => {
    if (!isRecording) {
      return startRecording();
    } else if (isPaused) {
      resumeRecording();
      return true;
    } else {
      pauseRecording();
      return true;
    }
  }, [isRecording, isPaused, startRecording, pauseRecording, resumeRecording]);

  return {
    // States
    isRecording,
    isPaused,
    duration,
    error,
    hasPermission,
    isSupported,
    recordedAudios,
    hasUnsavedRecordings,
    loadedAudios,
    isLoadingAudios,
    offlinePendingAudios,
    // Recording controls
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    toggleRecording,
    requestPermission,
    // Audio management
    deleteRecordedAudio,
    restoreAudio,
    clearAllRecordings,
    setLoadedAudios,
    refreshOfflineAudios,
    promotePendingToLoaded,
    // Utilities
    formatDuration,
    getRecordedChunks,
  };
};
